import React, { useEffect, useState } from 'react';
import { WORKER_API } from '../config.js';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (Math.abs(num) >= 1000) return `${num.toFixed(0)}%`;
  if (Math.abs(num) >= 100) return `${num.toFixed(1)}%`;
  return `${num.toFixed(2)}%`;
};

function ApyTokenModal({ isOpen, onClose, password }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchedAt, setFetchedAt] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const fetchTokenData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('admin_token') || '';
        const response = await fetch(`${WORKER_API}/admin/api/apy-by-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to load token APY data');
        }

        const result = await response.json();
        setTokens(Array.isArray(result.tokens) ? result.tokens : []);
        setFetchedAt(result.fetchedAt || null);
      } catch (err) {
        setError(err.message || 'Failed to load token APY data');
      } finally {
        setLoading(false);
      }
    };

    fetchTokenData();
  }, [isOpen, password]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div className="w-full max-w-5xl max-h-[90vh] rounded-2xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Token APY Overview</h3>
            {fetchedAt && (
              <p className="text-sm text-gray-500">
                Updated at {new Date(fetchedAt).toLocaleString()}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-4 flex-1 min-h-0">
          {loading ? (
            <div className="h-full flex items-center justify-center text-gray-500">
              Loading token APY data...
            </div>
          ) : error ? (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              {error}
            </div>
          ) : (
            <div className="h-full overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Token
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Pools
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Total TVL
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Avg APY
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Base / Reward
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Top Pool
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tokens.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-6 text-center text-gray-500">
                        No token APY data available.
                      </td>
                    </tr>
                  ) : (
                    tokens.map((token) => (
                      <React.Fragment key={token.token}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {token.token}
                          {token.chains && token.chains.length > 0 && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {token.chains.length} chain{token.chains.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {token.poolCount || 0}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          {formatNumber(token.tvlTotal || 0)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                          {formatPercent(token.avgApy)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">
                          <div>Base {formatPercent(token.avgBase)}</div>
                          <div>Reward {formatPercent(token.avgReward)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {token.maxPool ? (
                            <div className="space-y-1">
                              <div className="font-semibold">
                                {token.maxPool.project} · {token.maxPool.chain}
                              </div>
                              <div className="text-xs text-gray-500">
                                APY {formatPercent(token.maxPool.apy)} • TVL {formatNumber(token.maxPool.tvlUsd || 0)}
                              </div>
                              {token.maxPool.protocol ? (
                                <div className="text-xs text-blue-600">
                                  Protocol: {token.maxPool.protocol.name} (ID {token.maxPool.protocol.id})
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400">
                                  Protocol not matched
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                      {token.chains && token.chains.length > 0 && (
                        <tr>
                          <td colSpan="6" className="px-4 pb-4">
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Chain Breakdown
                              </div>
                              <div className="space-y-1.5">
                                {token.chains.map((chain) => (
                                  <div key={`${token.token}-${chain.chain}`} className="bg-white rounded-md border border-gray-100 px-3 py-2 space-y-1">
                                    <div className="flex items-center justify-between text-sm font-semibold text-gray-900">
                                      <span>{chain.chain}</span>
                                      <span className="text-xs text-gray-500">Pools: {chain.poolCount || 0}</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                      <span>TVL {formatNumber(chain.tvlTotal || 0)}</span>
                                      <span>Avg APY {formatPercent(chain.avgApy)}</span>
                                      <span>Base {formatPercent(chain.avgBase)}</span>
                                      <span>Reward {formatPercent(chain.avgReward)}</span>
                                    </div>
                                    {chain.maxPool && (
                                      <div className="text-xs text-blue-600">
                                        Top {formatPercent(chain.maxApy)} · {chain.maxPool.project} ({chain.maxPool.chain})
                                        {chain.maxPool.protocol ? (
                                          <span className="ml-1 text-gray-500">
                                            [Protocol: {chain.maxPool.protocol.name}]
                                          </span>
                                        ) : (
                                          <span className="ml-1 text-gray-400">
                                            [Protocol unmatched]
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {token.topPools && token.topPools.length > 0 && (
                        <tr>
                          <td colSpan="6" className="px-4 pb-6">
                            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Top Pools (Linked Protocols)
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {token.topPools.map((pool, idx) => (
                                  <div key={`${token.token}-pool-${idx}`} className="border border-gray-100 rounded-lg p-3 text-sm text-gray-800">
                                    <div className="font-semibold text-gray-900">
                                      {pool.project || 'Unknown project'}
                                      {pool.protocol ? (
                                        <span className="text-xs text-blue-600 ml-2">
                                          (Protocol: {pool.protocol.name} · Chain {pool.protocol.blockchain || '—'} · ID {pool.protocol.id})
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400 ml-2">
                                          (No protocol match)
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Chain {pool.chain || '—'} · Pool {pool.pool || '—'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      APY {formatPercent(pool.apy)} · Base {formatPercent(pool.apyBase)} · Reward {formatPercent(pool.apyReward)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      TVL {formatNumber(pool.tvlUsd || 0)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApyTokenModal;

